import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow, PageBand, CTABand, PractitionerCard, GoldRule } from "@/components/Shared";
import { ConciergeButton } from "@/components/Concierge";
import { TEAM, WHO_SHOULD_I_SEE, INTERNATIONAL_STANDFIRST } from "@/data/content";
import { PAGE_HEROES } from "@/data/photos";
import { ArrowRight } from "lucide-react";

const TeamGrid = () => (
  <section className="bg-warm-white" data-testid="team-grid-section">
    <div className="shell pb-16 lg:pb-20">
      <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" data-testid="team-grid">
        {TEAM.map((member, i) => (
          <PractitionerCard key={member.id} member={member} index={i} chips />
        ))}
      </div>
      <Reveal className="mt-14 flex flex-col sm:flex-row sm:items-center gap-5 border-t border-hairline/50 pt-10">
        <p className="font-serif italic text-deep-sea text-xl">Not sure where to start? That's what we're here for.</p>
        <ConciergeButton testId="team-consult-button" className="btn-gold px-7 py-3.5 text-sm font-medium tracking-[0.06em] self-start sm:self-auto" />
      </Reveal>
    </div>
  </section>
);

const Team = () => (
  <>
    <SEO
      title="The Lumière Team | Lumière Aesthetics, Cottesloe"
      description="Our physicians, a registered nurse and our practice manager. International expertise, here in Cottesloe — each profile tells you what they do and who to see first."
    />
    <PageBand
      eyebrow="The Lumière Team"
      title="The Lumière Team"
      standfirst="Our physicians, a registered nurse and our practice manager. Each profile tells you what they do, what they don't, and who to see first."
      image={PAGE_HEROES.team}
    />

    <section className="bg-warm-white" data-testid="team-international">
      <div className="shell pt-24 lg:pt-28 pb-6">
        <Reveal className="max-w-3xl">
          <Eyebrow className="mb-3">The Lumière Team</Eyebrow>
          <h2 className="text-deep-sea">
            International expertise, here in Cottesloe.
          </h2>
          <GoldRule className="mt-6" />
          <p className="standfirst" data-testid="team-international-standfirst">{INTERNATIONAL_STANDFIRST}</p>
          <h3 className="text-deep-sea mt-12 mb-10">Find your practitioner</h3>
        </Reveal>
      </div>
    </section>

    <TeamGrid />

    <section className="bg-cream" data-testid="team-note">
      <div className="shell py-12">
        <p className="text-soft-grey text-sm leading-relaxed max-w-2xl" data-testid="team-doctor-run-note">
          Skin, laser and device treatments are delivered by our registered nurse under doctor-written protocols.
          Each practitioner's profile shows the treatments they provide.
        </p>
      </div>
    </section>

    <section className="bg-warm-white" data-testid="who-should-i-see">
      <div className="max-w-3xl mx-auto px-8 py-24">
        <Reveal className="text-center mb-12">
          <h2 className="text-deep-sea">Who should I see?</h2>
          <GoldRule className="mx-auto mt-6" />
        </Reveal>
        <div className="space-y-1">
          {WHO_SHOULD_I_SEE.map((row, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <Link
                to={row.to}
                data-testid={`routing-${i}`}
                className="group flex items-center justify-between gap-6 py-5 border-b border-hairline/40"
              >
                <span className="text-ink/85 text-[15px]">{row.line}</span>
                <span className="flex items-center gap-2 text-sea-green group-hover:text-gold text-sm font-medium shrink-0 transition-colors duration-300">
                  {row.see}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 text-center">
          <p className="text-soft-grey text-sm">
            Not sure? Request a consultation and we'll match you to the right practitioner.
          </p>
        </Reveal>
      </div>
    </section>

    <CTABand />
  </>
);

export default Team;
